import type { ReactNode } from 'react';

/**
 * A small, safe Markdown renderer for blog bodies. Supports headings, lists,
 * blockquotes, images and paragraphs, with inline bold and links. It builds
 * React elements directly (never dangerouslySetInnerHTML), so authored content
 * cannot inject markup.
 */

/** Inline: **bold** and [text](href). */
function renderInline(text: string, keyBase: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const pattern = /\*\*(.+?)\*\*|\[(.+?)\]\((.+?)\)/g;
  let last = 0;
  let match: RegExpExecArray | null;
  let i = 0;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > last) nodes.push(text.slice(last, match.index));
    if (match[1] !== undefined) {
      nodes.push(<strong key={`${keyBase}-b${i}`}>{match[1]}</strong>);
    } else if (match[2] !== undefined && match[3] !== undefined) {
      const href = match[3];
      const internal = href.startsWith('/');
      nodes.push(
        <a
          key={`${keyBase}-a${i}`}
          href={href}
          {...(internal ? {} : { target: '_blank', rel: 'noreferrer' })}
          className="accent-text underline underline-offset-2"
        >
          {match[2]}
        </a>,
      );
    }
    last = pattern.lastIndex;
    i += 1;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

export function Markdown({ body }: { body: string }): JSX.Element {
  const lines = body.split('\n');
  const blocks: ReactNode[] = [];
  let list: string[] | null = null;
  let key = 0;

  const flushList = (): void => {
    if (list) {
      const items = list;
      blocks.push(
        <ul
          key={`ul${key++}`}
          className="my-4 list-disc space-y-1.5 pl-6 text-[var(--color-ink-soft)]"
        >
          {items.map((item, index) => (
            <li key={index}>{renderInline(item, `li${key}-${index}`)}</li>
          ))}
        </ul>,
      );
      list = null;
    }
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    const image = /^!\[(.*?)\]\((.+?)\)$/.exec(line.trim());

    if (line.startsWith('### ')) {
      flushList();
      blocks.push(
        <h3
          key={key++}
          className="mt-8 font-[var(--font-display)] text-xl font-semibold text-[var(--color-ink)]"
        >
          {renderInline(line.slice(4), `h3${key}`)}
        </h3>,
      );
    } else if (line.startsWith('## ')) {
      flushList();
      blocks.push(
        <h2
          key={key++}
          className="mt-10 font-[var(--font-display)] text-2xl font-semibold text-[var(--color-ink)]"
        >
          {renderInline(line.slice(3), `h2${key}`)}
        </h2>,
      );
    } else if (line.startsWith('> ')) {
      flushList();
      blocks.push(
        <blockquote
          key={key++}
          className="my-5 border-l-4 border-[var(--accent)] pl-4 text-lg italic text-[var(--color-ink)]"
        >
          {renderInline(line.slice(2), `bq${key}`)}
        </blockquote>,
      );
    } else if (line.startsWith('- ')) {
      (list ??= []).push(line.slice(2));
    } else if (image) {
      flushList();
      blocks.push(
        <img
          key={key++}
          src={image[2]}
          alt={image[1] ?? ''}
          className="my-6 w-full rounded-[var(--radius-2xl)] border border-[var(--color-line)]"
        />,
      );
    } else if (line.trim() === '') {
      flushList();
    } else {
      flushList();
      blocks.push(
        <p key={key++} className="my-4 leading-relaxed text-[var(--color-ink-soft)]">
          {renderInline(line, `p${key}`)}
        </p>,
      );
    }
  }
  flushList();

  return <div className="text-[17px]">{blocks}</div>;
}
